import React, { useState, useEffect } from "react";
import {
  Baby,
  GraduationCap,
  Heart,
  Activity,
  Sparkles,
  TrendingUp,
  Users,
  Shield,
  Clock,
  Target,
} from "lucide-react";

const AivyLifecycleAnimation = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [hoveredFeature, setHoveredFeature] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % 5);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const stages = [
    {
      icon: Baby,
      label: "성장발달",
      color: "#86efac",
      desc: "아이의 건강한 성장",
    },
    {
      icon: GraduationCap,
      label: "학습플래너",
      color: "#4ade80",
      desc: "맞춤형 교육 지원",
    },
    {
      icon: Heart,
      label: "건강관리",
      color: "#22d3ee",
      desc: "당뇨 등 건강 케어",
    },
    {
      icon: Activity,
      label: "일상케어",
      color: "#14b8a6",
      desc: "매일의 웰빙 지원",
    },
    {
      icon: Sparkles,
      label: "통합케어",
      color: "#06b6d4",
      desc: "삶의 질 향상",
    },
  ];

  const leftFeatures = [
    { icon: TrendingUp, label: "개인화 분석", desc: "실시간 데이터 기반" },
    { icon: Shield, label: "안심 보안", desc: "24시간 개인 데이터 모니터링" },
    { icon: Clock, label: "전 생애주기", desc: "평생 함께하는 AI" },
  ];

  const rightFeatures = [
    { icon: Users, label: "성장 발달", desc: "아동 성장 발달 관리" },
    { icon: Target, label: "맞춤 목표", desc: "개인별 최적화" },
    { icon: Heart, label: "정서 케어", desc: "마음까지 돌보는" },
  ];

  return (
    <div className="w-full h-full flex items-center justify-center px-4 py-6 mb-10">
      <div className="relative w-full max-w-[62rem] flex flex-col lg:flex-row items-center justify-center lg:justify-between gap-8">
        {/* 왼쪽 특징들 - 모바일에서는 상단으로 */}
        <div className="flex flex-row lg:flex-col gap-4 lg:gap-6 w-full lg:w-64 order-1 lg:order-1 overflow-x-auto lg:overflow-x-visible hover:overflow-x-auto group font-serif-kr">
          {leftFeatures.map((feature, index) => (
            <div
              key={feature.label}
              className="bg-white/80 backdrop-blur rounded-xl p-4 shadow-lg border border-emerald-200 min-w-[200px] lg:min-w-0 transition-all duration-300 hover:transform hover:translate-y-[-5px] hover:scale-105 cursor-pointer animate-slideInLeft"
              style={{ animationDelay: `${index * 0.2}s` }}
              onMouseEnter={() => setHoveredFeature(`left-${index}`)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-lg flex-shrink-0">
                  <feature.icon size={20} className="text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-gray-800 text-sm md:text-base truncate">
                    {feature.label}
                  </h4>
                  <p className="text-[12px] md:text-sm text-gray-600 truncate">
                    {feature.desc}
                  </p>
                </div>
              </div>
              {hoveredFeature === `left-${index}` && (
                <div className="mt-2 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full" />
              )}
            </div>
          ))}
        </div>

        {/* 중앙 애니메이션 */}
        <div className="relative w-full max-w-[400px] h-[400px] order-2 lg:order-2 flex-shrink-0 font-serif-kr">
          {/* 연결 효과 SVG */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400">
            <defs>
              <linearGradient
                id="featureGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#4ade80" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.2" />
              </linearGradient>
              <linearGradient
                id="connectionGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#4ade80" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.4" />
              </linearGradient>
            </defs>

            {/* 왼쪽 연결선들 */}
            {hoveredFeature?.startsWith("left") && (
              <path
                d="M 50,200 Q 150,200 200,200"
                stroke="url(#featureGradient)"
                strokeWidth="2"
                fill="none"
                className="animate-drawLine"
              />
            )}

            {/* 오른쪽 연결선들 */}
            {hoveredFeature?.startsWith("right") && (
              <path
                d="M 350,200 Q 250,200 200,200"
                stroke="url(#featureGradient)"
                strokeWidth="2"
                fill="none"
                className="animate-drawLine"
              />
            )}

            {/* 궤도 원 */}
            <circle
              cx="200"
              cy="200"
              r="120"
              fill="none"
              stroke="url(#connectionGradient)"
              strokeWidth="1"
              strokeDasharray="3,3"
              opacity="0.3"
            />
          </svg>

          {/* 중앙 Aivy 코어 */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full w-24 h-24 blur-xl opacity-20 animate-pulse"></div>
              <div className="relative bg-white rounded-full w-20 h-20 shadow-lg flex items-center justify-center border-2 border-emerald-400">
                <div className="text-lg font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent font-serif-en">
                  Aivy
                </div>
              </div>
            </div>
          </div>

          {/* 스테이지 아이콘들 */}
          {stages.map((stage, index) => {
            const angle = ((index * 72 - 90) * Math.PI) / 180;
            const x = 120 * Math.cos(angle);
            const y = 120 * Math.sin(angle);
            const isActive = activeStage === index;

            return (
              <div
                key={stage.label}
                className={`absolute transition-all duration-300 ${
                  isActive ? "scale-110" : ""
                }`}
                style={{
                  left: "50%",
                  top: "50%",
                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                }}
              >
                <div className={`relative ${isActive ? "animate-float" : ""}`}>
                  {/* 배경 글로우 */}
                  <div
                    className={`absolute inset-0 rounded-full blur-md ${
                      isActive ? "w-14 h-14 -m-3" : "w-12 h-12 -m-2"
                    }`}
                    style={{
                      backgroundColor: stage.color,
                      opacity: isActive ? 0.3 : 0.1,
                    }}
                  ></div>

                  {/* 아이콘 컨테이너 */}
                  <div
                    className={`relative bg-white rounded-full shadow-md flex items-center justify-center ${
                      isActive ? "w-14 h-14" : "w-12 h-12"
                    }`}
                    style={{ borderColor: stage.color, borderWidth: "2px" }}
                  >
                    <stage.icon
                      size={isActive ? 24 : 20}
                      color={stage.color}
                      strokeWidth={2}
                    />
                  </div>

                  {/* 라벨 - 활성화된 것만 */}
                  {isActive && (
                    <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 text-center">
                      <div className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                        {stage.label}
                      </div>
                      <div className="text-[12px] text-gray-500 whitespace-nowrap">
                        {stage.desc}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* 데이터 흐름 점들 */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {[...Array(10)].map((_, i) => {
              const angle = (i * 36 * Math.PI) / 180;
              const radius = 80 + Math.random() * 40;
              const x = 200 + radius * Math.cos(angle);
              const y = 200 + radius * Math.sin(angle);
              return (
                <circle
                  key={`dot-${i}`}
                  cx={x}
                  cy={y}
                  r="2"
                  fill={i % 2 === 0 ? "#4ade80" : "#14b8a6"}
                  opacity="0.4"
                  className="animate-pulse"
                  style={{ animationDelay: `${i * 0.3}s` }}
                />
              );
            })}
          </svg>
        </div>

        {/* 오른쪽 특징들 - 모바일에서는 하단으로 */}
        <div className="flex flex-row lg:flex-col gap-4 lg:gap-6 w-full lg:w-64 order-3 lg:order-3 overflow-x-auto lg:overflow-x-visible hover:overflow-x-auto group font-serif-kr">
          {rightFeatures.map((feature, index) => (
            <div
              key={feature.label}
              className="bg-white/80 backdrop-blur rounded-xl p-4 shadow-lg border border-cyan-200 min-w-[200px] lg:min-w-0 transition-all duration-300 hover:transform hover:translate-y-[-5px] hover:scale-105 cursor-pointer animate-slideInRight"
              style={{ animationDelay: `${index * 0.2}s` }}
              onMouseEnter={() => setHoveredFeature(`right-${index}`)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-cyan-100 to-emerald-100 rounded-lg flex-shrink-0">
                  <feature.icon size={20} className="text-cyan-600" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-gray-800 text-sm md:text-base truncate">
                    {feature.label}
                  </h4>
                  <p className="text-[12px] md:text-sm text-gray-600 truncate">
                    {feature.desc}
                  </p>
                </div>
              </div>
              {hoveredFeature === `right-${index}` && (
                <div className="mt-2 h-1 bg-gradient-to-r from-cyan-400 to-emerald-400 rounded-full" />
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes slideInLeft {
          from { 
            opacity: 0;
            transform: translateX(-30px);
          }
          to { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from { 
            opacity: 0;
            transform: translateX(30px);
          }
          to { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes drawLine {
          0% { 
            stroke-dasharray: 100;
            stroke-dashoffset: 100;
          }
          100% { 
            stroke-dasharray: 100;
            stroke-dashoffset: 0;
          }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-slideInLeft {
          animation: slideInLeft 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-drawLine {
          animation: drawLine 1s ease-out forwards;
        }
        
        @media (max-width: 1024px) {
          .animate-slideInLeft,
          .animate-slideInRight {
            animation: none;
            opacity: 1;
          }
          
          /* 모바일에서 스크롤바 숨기기 */
          .group {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          
          .group::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
          
          /* 호버 시에만 스크롤바 표시 */
          .group:hover {
            scrollbar-width: auto;
            -ms-overflow-style: auto;
            padding-bottom: 16px; /* 스크롤바와 컨텐츠 사이 간격 */
          }
          
          .group:hover::-webkit-scrollbar {
            display: block;
            height: 6px;
          }
          
          .group:hover::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
            margin: 0 4px;
          }
          
          .group:hover::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          .group:hover::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        }
      `}</style>
    </div>
  );
};

export default AivyLifecycleAnimation;
